import { useState, useEffect } from "react";
import {
  Box,
  Alert,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Stack,
} from "@mui/material";
import adminAPI, { AdminUser, UserAiUsage } from "../admin.api";
import { summarizeAiUsage } from "../aiUsageSummary";
import { aiGatewayInsightsUrl } from "../aiGatewayLink";

interface UserAiUsageTabProps {
  user: AdminUser;
}

function UserAiUsageTab({ user }: UserAiUsageTabProps) {
  const [aiUsage, setAiUsage] = useState<UserAiUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAiUsage();
  }, [user._id]);

  const fetchAiUsage = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminAPI.getUserAiUsage(user._id);
      setAiUsage(data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || "Failed to fetch AI usage");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !aiUsage) {
    return <Alert severity="error">{error || "Failed to load AI usage"}</Alert>;
  }

  const summary = summarizeAiUsage(aiUsage.usage);
  const window = `LAST ${aiUsage.days} DAYS`;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cost, requests and words this user generated through the AI Gateway.{" "}
        <a href={aiGatewayInsightsUrl()} target="_blank" rel="noopener noreferrer">
          Open the gateway dashboard
        </a>
        .
      </Typography>

      {aiUsage.usage.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No attributed AI requests in this window.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  AI COST ({window})
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  ${aiUsage.totals.cost.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${summary.averageDailyCost.toFixed(2)} per active day
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  WORDS GENERATED ({window})
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {aiUsage.totals.words.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Estimated from output tokens
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  AI REQUESTS ({window})
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {aiUsage.totals.requests.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Across {summary.activeDays} active day
                  {summary.activeDays === 1 ? "" : "s"}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  BUSIEST DAY
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {summary.busiestDate ?? "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Highest AI spend in this window
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UserAiUsageTab;
